import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { errorToMessage } from "@/lib/apiClient";
import { LogIn, Loader2, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(username.trim(), password);
      // Route to the first landing page the user can actually access.
      const role = u?.role;
      const roleHome = {
        SUPER_ADMIN: "/admin/dashboard",
        ADMIN: "/admin/dashboard",
        RECEPTION: "/admin/cases",
        SALES: "/admin/sales",
        ACCOUNTS: "/admin/sales",
        QC_ENGINEER: "/admin/qc/jobs",
        PREPARATION_ENGINEER: "/admin/qc/jobs",
        OPERATIONS_ENGINEER: "/admin/qc/jobs",
        BATTERY_ENGINEER: "/admin/cases",
        WAREHOUSE: "/admin/inventory",
        AI_WARRANTY_AGENT: "/admin/cases",
      }[role] || "/admin/dashboard";
      const from = location.state?.from || roleHome;
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorToMessage(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="text-3xl font-semibold tracking-tight">RentOPC OS</div>
          <div className="mt-1 text-sm text-slate-400">Enterprise Operations Console</div>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="bg-slate-900 border border-slate-800 rounded-lg shadow-2xl p-6 space-y-4"
        >
          <div>
            <label className="block text-sm text-slate-300 mb-1">Username</label>
            <input
              data-testid="login-username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 focus:outline-none focus:border-slate-500"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={1}
              placeholder="e.g. admin"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Password</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 pr-10 focus:outline-none focus:border-slate-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={1}
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-200 focus:outline-none focus:text-slate-200"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 font-medium px-3 py-2"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}
